import {
  DEAL_PAYROLL_CALCULATION_REVISION, DEAL_PAYROLL_STATUS, DealPayrollBreakdown,
  DealPayrollProfile, DealPayrollTaskLine, isFinitePayrollNumber
} from '../../../models/deal-payroll.model';

const CENTS_PER_UNIT = 100;
const ZERO = 0;
const MONTH_LENGTH = 7;
const MANUFACTURING_PROFILES: DealPayrollProfile[] = [
  'MANUFACTURING_AREA', 'CONSTRUCTION_TEAM', 'FIXED_COEFFICIENT', 'TASK_GRADE'
];
const CHECKLIST_RULE = 'PROCUREMENT_CHECKLIST';
const CHECKLIST_UNIT = 'CHECKLIST';
const UNKNOWN_REASON = 'DEAL_PAYROLL_COMPONENTS_UNAVAILABLE';
const NO_PLAN_REASON = 'DEAL_PAYROLL_NO_PLAN_FULL_RATE';
const OPEN_REASON = 'DEAL_PAYROLL_PLAN_NOT_CLOSED';
const OTHER_MONTH_REASON = 'DEAL_PAYROLL_PLAN_NOT_PAID_THIS_MONTH';
const PAID_REASON = 'DEAL_PAYROLL_PLAN_PAID_THIS_MONTH';

export interface DealPayrollComponentSummary {
  plannedAmount: number;
  actualAmount: number;
  total: number;
  plannedPercent: number;
}

export function hasTaskComponents(task: DealPayrollTaskLine): boolean {
  return !!task && task.taskRuleCode !== CHECKLIST_RULE && task.coefficientUnitType !== CHECKLIST_UNIT
    && isFinitePayrollNumber(task.plannedAmount) && isFinitePayrollNumber(task.actualAmount)
    && isFinitePayrollNumber(task.result)
    && cents(task.plannedAmount) + cents(task.actualAmount) === cents(task.result);
}

export function componentSummary(value: DealPayrollBreakdown): DealPayrollComponentSummary | null {
  if (!isCompleteComponentSet(value)) { return null; }
  const planned = value.taskLines.reduce((sum, line) => sum + cents(line.plannedAmount), ZERO);
  const actual = value.taskLines.reduce((sum, line) => sum + cents(line.actualAmount), ZERO);
  const total = planned + actual;
  if (!isFinitePayrollNumber(value.calculatedTaskAmount) || cents(value.calculatedTaskAmount) !== total) { return null; }
  return {plannedAmount: planned / CENTS_PER_UNIT, actualAmount: actual / CENTS_PER_UNIT,
    total: total / CENTS_PER_UNIT, plannedPercent: total > ZERO
      ? Math.max(ZERO, Math.min(CENTS_PER_UNIT, planned * CENTS_PER_UNIT / total)) : ZERO};
}

function isCompleteComponentSet(value: DealPayrollBreakdown): boolean {
  return !!value && value.status === DEAL_PAYROLL_STATUS.COMPLETE
    && value.calculationRevision === DEAL_PAYROLL_CALCULATION_REVISION
    && MANUFACTURING_PROFILES.indexOf(value.payrollProfile) >= ZERO && Array.isArray(value.taskLines)
    && isFinitePayrollNumber(value.taskLineCount) && value.taskLineCount === value.taskLines.length
    && value.taskLines.every(hasTaskComponents);
}

export function planAccruesInMonth(task: DealPayrollTaskLine, month: string): boolean | null {
  if (task.completed === false) { return false; }
  if (task.completed !== true || !task.closedAt) { return null; }
  const accrualMonth = task.plannedAccrualMonth || task.closedAt.substring(ZERO, MONTH_LENGTH);
  return accrualMonth === month;
}

export function taskPlanReason(task: DealPayrollTaskLine, month: string): string {
  if (!hasTaskComponents(task)) { return UNKNOWN_REASON; }
  if (!isFinitePayrollNumber(task.plannedHours) || task.plannedHours <= ZERO) { return NO_PLAN_REASON; }
  const paid = planAccruesInMonth(task, month);
  if (paid === null) { return UNKNOWN_REASON; }
  if (task.completed === false) { return OPEN_REASON; }
  return paid ? PAID_REASON : OTHER_MONTH_REASON;
}

function cents(amount: number): number {
  return Math.round(amount * CENTS_PER_UNIT);
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/my-working-report/deal-payroll-detail/deal-payroll-detail.components.ts